import React, {useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {VStack, Stack, Box, Text, useMultiStyleConfig, StackProps, TextProps} from '@chakra-ui/react'
import {Picture, Button, ButtonProps, Select} from '_uiComponents'
import {useViewport, useAnalytics, useCustomApi} from '_customHooks'
import {useModal} from '_context'
import {setCountrySelectionCookie} from '_utils'

interface SwitchLocaleModalProps {
    labels: {
        title: string
        description: string
        imageUrl: {
            desktop: string
            tablet: string
            mobile: string
        }
    }
    locale: {
        countries: {label: string; value: string; hostlink: string}[]
        suggested: {label: string; value: string}
        current: {label: string; value: string}
    }
}

/**
 * @name SwitchLocaleModal
 *
 * @component
 *
 * @description
 * Component designed to facilitate the switching of locale settings within an application.
 * It provides users with options to change their current locale or stay with the suggested locale, as well as the ability to select from a list of available countries.
 *
 * @param {SwitchLocaleModalProps} props - The component props.
 * @param {Object} props.labels - Contains text labels and image URLs for the modal.
 * @param {string} props.labels.title - Title text displayed at the top of the modal.
 * @param {string} props.labels.description - Description text providing context or instructions to the user.
 * @param {Object} props.labels.imageUrl - Object containing URLs for images used in the modal, differentiated by device type.
 * @param {string} props.labels.imageUrl.desktop - Image URL for desktop view.
 * @param {string} props.labels.imageUrl.tablet - Image URL for tablet view.
 * @param {string} props.labels.imageUrl.mobile - Image URL for mobile view.
 * @param {Object} props.locale - Contains locale information for countries, suggested locale, and current locale.
 * @param {Array<Object>} props.locale.countries - Array of country objects available for selection, each with a label and value.
 * @param {Object} props.locale.suggested - Suggested locale object with label and value.
 * @param {Object} props.locale.current - Current locale object with label and value.
 *
 * @returns {JSX.Element} A modal component that allows users to switch their locale settings.
 */
const SwitchLocaleModal: React.FC<SwitchLocaleModalProps> = ({labels, locale}) => {
    const styles = useMultiStyleConfig('SwitchLocaleModal') as Record<string, StackProps & TextProps>
    const {isMobile} = useViewport()
    const {closeModal} = useModal()
    const {analyticsEventCheckLocale} = useAnalytics()
    const {formatMessage} = useIntl()
    const {useSetLocale} = useCustomApi()
    const {mutate} = useSetLocale()

    const [activeStep, setActiveStep] = useState(0)

    const bannerText = labels.description
    const modalTitle = labels.title

    const imageUrl = labels.imageUrl
    const imageDesktop = imageUrl.desktop
    const imageTablet = imageUrl.tablet
    const imageMobile = imageUrl.mobile

    const altImgLabel = formatMessage({id: 'locale.alt.image', defaultMessage: 'Immagine del pop-up cambio locale'})
    const buttonChangeLabel = formatMessage({id: 'label.yesno.formgeo.button.yes', defaultMessage: 'VAI AL SITO {0}'}, {0: locale.suggested.label})
    const buttonStayLabel = formatMessage({id: 'label.yesno.formgeo.button.no', defaultMessage: 'CONTINUA SU {0}'}, {0: locale.current.label})
    const switchLabel = formatMessage({id: 'label.yesno.formgeo.others', defaultMessage: 'Cambia Paese/Lingua'})
    const deliveryCountryLabel = formatMessage({id: 'label.formgeo.country', defaultMessage: 'Paese di consegna'})
    const confirmLabel = formatMessage({id: 'label.formgeo.button', defaultMessage: 'Conferma'})
    const currentLocale = locale.current.value
    const suggestedLocale = locale.suggested.value

    const handleChangeLocale = (localeOption?: string) => {
        let targetLocale = localeOption || suggestedLocale
        const targetLocaleObject = locale.countries.find((a) => a.value === targetLocale)
        targetLocale = targetLocale.replace('_', '-')
        const targetLocaleHostlink = targetLocaleObject?.hostlink ?? ''
        const targetLocaleAlias = targetLocale.replace('ch-', 'zh-').toLowerCase()

        const dataAction = {
            action: document.querySelector('div[data-action]')?.getAttribute('data-action') || undefined,
            queryParams: document.querySelector('div[data-cgid]')?.getAttribute('data-cgid') || undefined,
        }

        const eventAction = localeOption ? 'confirmchange' : 'confirm'
        const eventLabel = localeOption ? localeOption.split('_')[1] : suggestedLocale.split('_')[1]

        setCountrySelectionCookie(eventLabel)

        analyticsEventCheckLocale({
            action: eventAction,
            label: eventLabel,
        })

        mutate(
            {locale: targetLocale, dataAction: dataAction},
            {
                onSuccess: (data) => {
                    const switchDomain = !window.location.origin.includes(targetLocaleHostlink) && targetLocaleHostlink !== ''
                    if (data.success) {
                        const relativePath = data.redirectUrl

                        if (switchDomain) {
                            window.location.href = `${window.location.protocol}//${targetLocaleHostlink}/${targetLocaleAlias}${relativePath}`
                        } else {
                            window.location.href = `/${targetLocaleAlias}${relativePath}`
                        }
                    } else {
                        const currentPath = window.location.pathname
                        const newPath = currentPath.replace(/\/[a-z]{2}-[a-z]{2}/i, `/${targetLocaleAlias}`)

                        if (switchDomain) {
                            window.location.href = `${window.location.protocol}//${targetLocaleHostlink}${newPath}`
                        } else {
                            window.location.href = newPath
                        }
                    }
                },
            },
        )
    }

    const handleSwitchCountry = () => {
        setActiveStep(activeStep + 1)

        analyticsEventCheckLocale({
            action: 'change',
            label: '',
        })
    }

    useEffect(() => {
        analyticsEventCheckLocale({
            action: 'start',
            label: suggestedLocale.split('_')[1],
        })
    }, [])

    return (
        <VStack {...styles.wrapper} data-testid="switch-locale-modal">
            <Box {...styles.imageWrapper}>
                <Picture imagelg={imageDesktop} imagemd={imageTablet} imagexs={imageMobile} position="absolute" alt={altImgLabel} />
                <Text {...styles.bannerText}>{bannerText}</Text>
            </Box>
            <VStack {...styles.body}>
                <Text {...styles.title}>{modalTitle}</Text>
                {activeStep === 0 ? (
                    <>
                        <Stack {...styles.buttonWrapper}>
                            <Button
                                variant="primary"
                                {...(styles.button as ButtonProps)}
                                onClick={() => handleChangeLocale()}
                                data-testid="changeLocale-submit"
                            >
                                {buttonChangeLabel}
                            </Button>
                            <Button
                                variant="secondary"
                                {...(styles.button as ButtonProps)}
                                onClick={() => {
                                    closeModal()
                                }}
                            >
                                {buttonStayLabel}
                            </Button>
                        </Stack>
                        <Button
                            variant="textLink"
                            size="xs"
                            {...(styles.switchLink as ButtonProps)}
                            onClick={() => handleSwitchCountry()}
                            data-testid="switch-label"
                        >
                            {switchLabel}
                        </Button>
                    </>
                ) : (
                    <VStack
                        {...styles.selectWrapper}
                        as="form"
                        onSubmit={(e) => {
                            e.preventDefault()
                            const formData = new FormData(e.currentTarget as unknown as HTMLFormElement)
                            const selectedCountry = formData.get('delivery-country')
                            handleChangeLocale(selectedCountry as string)
                        }}
                    >
                        <Select
                            name="delivery-country"
                            label={deliveryCountryLabel}
                            options={locale.countries}
                            defaultValue={currentLocale}
                            dataTestId="delivery-country"
                        />
                        <Button
                            variant="primary"
                            type="submit"
                            width={!isMobile ? 'xs' : undefined}
                            {...(styles.secondStepButton as ButtonProps)}
                            data-testid="confirm-button"
                        >
                            {confirmLabel}
                        </Button>
                    </VStack>
                )}
            </VStack>
        </VStack>
    )
}

export {SwitchLocaleModal}
